<script>
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'

import axios from 'axios'
import { isPostureFinding, postureExclusionNote } from '@utils/sla-contract'
import percentile from '@utils/percentile'
import formatDuration from '@utils/format-duration'
import { SEVERITIES, bogotaDay } from '@utils/severities'

const DAY = 24 * 60 * 60 * 1000

const STATUS_LABELS = {
  resolved_true_positive: 'True positive',
  resolved_false_positive: 'False positive',
  resolved_auto_resolve: 'Auto-resolved',
  resolved_duplicate: 'Duplicate',
  resolved_known_issue: 'Known issue',
  resolved_other: 'Other'
}

const statusLabel = status => STATUS_LABELS[status] || status.replace(/^resolved_/, '').replace(/_/g, ' ')

export default {
  page: {
    title: 'Service SLO',
    meta: [{ name: 'description', content: 'description' }]
  },
  components: { Layout, PageHeader },
  data () {
    return {
      title: 'Service SLO',
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      items: [
        {
          text: 'Home',
          href: '/'
        },
        {
          text: 'Service SLO',
          active: true
        }
      ],
      data: [],
      excludedCount: 0,
      loadedAt: Date.now(),
      loading: true,
      loadError: false
    }
  },
  computed: {
    kpis () {
      const times = this.data.map(inc => inc.resolve_time_minutes)
      return [
        { key: 'count', label: 'Resolved incidents', icon: 'check-circle', value: this.data.length.toLocaleString() },
        {
          key: 'median',
          label: 'Median resolve time',
          icon: 'clock',
          value: formatDuration(percentile(times, 0.5)),
          help: 'Half of the resolved incidents were closed within this time. The median is used so a few very long cases do not distort the figure.'
        },
        {
          key: 'p90',
          label: '90% resolved within',
          icon: 'bar-chart-2',
          value: formatDuration(percentile(times, 0.9)),
          help: '90% of the resolved incidents were closed within this time (90th percentile). Only the slowest 10% took longer.'
        }
      ]
    },
    // Severities without resolved incidents are hidden once the data is loaded
    severityStats () {
      return SEVERITIES
        .map(severity => {
          const times = this.data.filter(inc => inc.severity === severity.key).map(inc => inc.resolve_time_minutes)
          return { ...severity, count: times.length, median: percentile(times, 0.5), p90: percentile(times, 0.9) }
        })
        .filter(severity => this.loading || severity.count > 0)
    },
    // Slowest status first
    statusStats () {
      const byStatus = {}
      this.data.forEach(inc => {
        (byStatus[inc.status] = byStatus[inc.status] || []).push(inc.resolve_time_minutes)
      })
      return Object.keys(byStatus)
        .map(status => ({ status, label: statusLabel(status), count: byStatus[status].length, median: percentile(byStatus[status], 0.5) }))
        .sort((first, second) => second.median - first.median)
    },
    days () {
      // The data window is the last 30 x 24h, which touches 31 calendar days
      const days = []
      for (let ms = this.loadedAt - 30 * DAY; ms <= this.loadedAt; ms += DAY) days.push(bogotaDay(ms))
      return days
    },
    // Resolved incidents per day of resolution (last modification), by severity
    dailySeries () {
      const counts = {}
      this.data.forEach(inc => {
        const key = `${bogotaDay(inc.modification_ms)}|${inc.severity}`
        counts[key] = (counts[key] || 0) + 1
      })
      return SEVERITIES.map(severity => ({
        name: severity.label,
        data: this.days.map(day => counts[`${day}|${severity.key}`] || 0)
      }))
    },
    dailyOptions () {
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit' },
        colors: SEVERITIES.map(severity => severity.color),
        plotOptions: {
          bar: { columnWidth: '60%', borderRadius: 3, borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last' }
        },
        // 1px white separation between stacked segments
        stroke: { show: true, width: 1, colors: ['#ffffff'] },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'left', markers: { radius: 2 } },
        grid: { borderColor: '#eef0f3', xaxis: { lines: { show: false } } },
        xaxis: {
          categories: this.days.map(day => day.slice(5)),
          tickAmount: 10,
          labels: { rotate: 0, style: { colors: '#98a6ad' } },
          axisTicks: { show: false }
        },
        yaxis: { labels: { formatter: value => Math.round(value), style: { colors: '#98a6ad' } } },
        tooltip: { shared: true, intersect: false, y: { formatter: value => `${value} resolved` } }
      }
    },
    exclusionNote () {
      return postureExclusionNote(this.excludedCount)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    formatDuration,
    loadData () {
      let that = this
      let baseURL = that.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      that.loading = true
      that.loadError = false
      axios.get(`${baseURL}/pan/xdr/service/resolved?include=incident.incident&created_after_minutes=43200&limit=9999`).then((response) => {
        if (response.status === 200) {
          const toMs = value => (value && value['$date'] ? new Date(value['$date']).getTime() : null)
          that.loadedAt = Date.now()
          const incidents = response.data.data.map(function (entry) {
            return {
              incident_id: entry.incident_id,
              status: entry.status,
              severity: entry.severity,
              modification_ms: toMs(entry.modification_time),
              resolve_time_minutes: entry.resolve_time_minutes,
              incident_sources: entry.incident_sources || []
            }
          })
          // Cloud posture findings are outside the MDR service scope
          const inScope = incidents.filter(inc => !isPostureFinding(inc.incident_sources))
          that.excludedCount = incidents.length - inScope.length
          // Client security tests (pentests) follow their own process and are not part of the SLO
          that.data = inScope.filter(inc => inc.status !== 'resolved_security_testing')
        }
      })
        .catch(function (error) {
          that.loadError = true
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('idToken')
            that.$router.push({ name: 'Home' })
          }
        })
        .finally(function () {
          that.loading = false
        })
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader
      :title="title"
      :items="items"
    />
    <b-alert
      :show="loading"
      variant="info"
      class="d-flex align-items-center"
    >
      <b-spinner
        small
        class="mr-2"
      />
      Loading incidents from the last 30 days, this can take a few seconds...
    </b-alert>
    <b-alert
      :show="loadError && !loading"
      variant="danger"
      class="d-flex align-items-center justify-content-between"
    >
      Could not load the SLO data. Please try again.
      <b-button
        size="sm"
        variant="outline-danger"
        @click="loadData"
      >
        Retry
      </b-button>
    </b-alert>

    <div class="slo-page">
      <p
        v-if="!loading && !loadError"
        class="text-muted mb-3"
      >
        Last 30 days · {{ data.length.toLocaleString() }} resolved incidents
        <span
          v-if="excludedCount"
          v-b-tooltip.hover
          :title="exclusionNote"
          class="help-tip ml-1"
        >
          <feather
            type="info"
            class="icon-xs"
          />
        </span>
      </p>

      <!-- KPI tiles -->
      <div class="row">
        <div
          v-for="kpi in kpis"
          :key="kpi.key"
          class="col-xl-4 col-md-6 mb-4"
        >
          <div class="card h-100 mb-0">
            <div class="card-body">
              <div class="d-flex align-items-start">
                <h5 class="flex-grow-1 text-muted font-size-13 mb-1">
                  {{ kpi.label }}
                  <span
                    v-if="kpi.help"
                    v-b-tooltip.hover
                    :title="kpi.help"
                    class="help-tip ml-1"
                  >
                    <feather
                      type="help-circle"
                      class="icon-xs"
                    />
                  </span>
                </h5>
                <div class="avatar-sm rounded-circle bg-soft-primary">
                  <span class="avatar-title bg-transparent text-primary">
                    <feather
                      :type="kpi.icon"
                      class="icon-sm"
                    />
                  </span>
                </div>
              </div>
              <b-skeleton
                v-if="loading"
                width="50%"
                height="2rem"
                class="mt-2"
              />
              <h3
                v-else
                class="mt-2 mb-0"
              >
                {{ loadError ? '—' : kpi.value }}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily resolved + resolve time by severity -->
      <div class="row">
        <div class="col-xl-8 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="font-size-14 mb-1">
                <feather
                  type="bar-chart-2"
                  class="icon-xs mr-1 text-muted"
                />
                Resolved per day
              </h5>
              <b-skeleton-img
                v-if="loading"
                no-aspect
                height="280px"
              />
              <apexchart
                v-else-if="!loadError"
                type="bar"
                height="280"
                :series="dailySeries"
                :options="dailyOptions"
              />
            </div>
          </div>
        </div>
        <div class="col-xl-4 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="font-size-14 mb-3">
                <feather
                  type="clock"
                  class="icon-xs mr-1 text-muted"
                />
                Resolve time by severity
              </h5>
              <div class="d-flex text-muted font-size-12 pb-1 border-bottom">
                <span class="flex-grow-1">Severity</span>
                <span class="slo-col">Median</span>
                <span class="slo-col">90% within</span>
              </div>
              <div
                v-for="severity in severityStats"
                :key="severity.key"
                class="d-flex align-items-center py-2 border-bottom"
              >
                <span class="flex-grow-1">
                  <span
                    class="slo-swatch mr-2"
                    :style="{ background: severity.color }"
                  />
                  {{ severity.label }}
                  <span
                    v-if="!loading && !loadError"
                    class="text-muted font-size-12 ml-1"
                  >({{ severity.count.toLocaleString() }})</span>
                </span>
                <b-skeleton
                  v-if="loading"
                  width="110px"
                />
                <template v-else-if="!loadError">
                  <strong class="slo-col">{{ formatDuration(severity.median) }}</strong>
                  <span class="slo-col text-muted">{{ formatDuration(severity.p90) }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Median by status -->
      <div class="card">
        <div class="card-body">
          <h5 class="font-size-14 mb-3">
            <feather
              type="tag"
              class="icon-xs mr-1 text-muted"
            />
            Median resolve time by status
          </h5>
          <b-skeleton
            v-if="loading"
            width="60%"
          />
          <div
            v-else-if="!loadError"
            class="row"
          >
            <div
              v-for="status in statusStats"
              :key="status.status"
              class="col-md-4 mb-2"
            >
              <span class="text-muted">{{ status.label }} ({{ status.count.toLocaleString() }})</span>
              <h3 class="mt-1 mb-0">
                {{ formatDuration(status.median) }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Thin outline keeps the light medium-yellow swatch visible on white */
.slo-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
}
.slo-col {
  width: 80px;
  text-align: right;
}
.help-tip {
  color: #ced4da;
  cursor: help;
}
.help-tip:hover {
  color: #98a6ad;
}
</style>
